import { TFunction } from 'react-i18next'
import type { ComboboxItem } from '@node-space/ui-components'
import type { GroupedLimit, Limit } from 'types/paymentLimits'
import { LimitType } from 'types/paymentLimits'

export const LIMIT_TYPE_LABELS = {
  TRANSACTION: 'paymentLimits.transactionLimit',
  DAILY: 'paymentLimits.dailyLimit',
} satisfies Record<Limit['limitType'], string>

export const PAYMENT_METHOD_LABELS: Record<string, string> = {
  SEPA_CT: 'SEPA',
  SEPA_INST: 'SEPA Instant',
  SWIFT: 'SWIFT',
  FASTER_PAYMENTS: 'Faster Payments',
  ACH: 'ACH',
  FEDWIRE: 'Fedwire',
  FEDNOW: 'FedNow',
  CRNOW: 'CRNow',
  RTP: 'RTP',
}

export const CURRENCIES = ['EUR', 'GBP', 'USD'] as const

export const PAYMENT_METHOD_OPTIONS: ComboboxItem[] = Object.entries(PAYMENT_METHOD_LABELS).map(
  ([value, leftText]) => ({ value, leftText })
)

export const getLimitTypeOptions = (t: TFunction<'translation', undefined>) => [
  { value: LimitType.TRANSACTION, label: t('paymentLimits.transactionLimit') },
  { value: LimitType.DAILY, label: t('paymentLimits.dailyLimit') },
]

export const getAmountLabel = (
  limitType: string,
  t: TFunction<'translation', undefined>
): string => {
  if (limitType === LimitType.DAILY) return t('paymentLimits.dailyLimit')
  return t('paymentLimits.transactionLimit')
}

export const formatPaymentMethodLabel = (method: string): string => {
  return PAYMENT_METHOD_LABELS[method] ?? method.replaceAll('_', ' ')
}

export const groupLimitsByMethodAndCurrency = (limits: Limit[]): GroupedLimit[] => {
  const grouped = limits.reduce<Record<string, GroupedLimit>>((groupedLimits, limit) => {
    const method = limit.method ?? limit.qualifiers?.method

    if (!method) {
      return groupedLimits
    }

    const key = `${method}-${limit.currency}`

    if (!groupedLimits[key]) {
      groupedLimits[key] = {
        method,
        currency: limit.currency,
      }
    }

    if (limit.limitType === LimitType.TRANSACTION) {
      groupedLimits[key].transactionLimit = limit
    } else if (limit.limitType === LimitType.DAILY) {
      groupedLimits[key].dailyLimit = limit
    }

    return groupedLimits
  }, {})

  return Object.values(grouped)
}
